import React, { useEffect, useState, useCallback } from "react";
import {
  disableAutoRenewal,
  enableAutoRenewal,
  upgradeStudent,
} from "../../student_dashboard/api";
import { axiosWithToken, functionBaseUrl } from "../../../common/firebase";
import { userService } from "../../../services/user";

const useStudents = ({
  user,
  tableOptions,
  search,
  fetchUser,
  fetchCoachCreditOverview,
}) => {
  const [students, setStudents] = React.useState(null);
  const [isStudentsLoading, setIsStudentsLoading] = useState(true);
  const [totalStudents, setTotalStudents] = useState(0);

  const getStudents = useCallback(
    async ({
      scope,
      masterId,
      coachId,
      page,
      sort,
      order,
      search,
      subscriptionFilter,
      limit = 10,
      customSort = true,
    }) => {
      const offset = (page - 1) * limit;

      const res = await axiosWithToken(
        `${functionBaseUrl}/api/v1/web/students-for-credits-page`,
        {
          method: "post",
          headers: { "Content-Type": "application/json" },
          data: {
            scope,
            masterId,
            coachId,
            search,
            subscriptionFilter,
            sort,
            order,
            offset,
            limit,
            customSort,
          },
        }
      );

      return res.data;
    },
    []
  );

  const fetchStudents = useCallback(async () => {
    if (!user?.id) return;

    try {
      const subscriptionFilter = tableOptions?.subscriptionFilter
        ? tableOptions.subscriptionFilter.split(":")
        : "";

      const commonParams = {
        search: search || "",
        page: tableOptions?.page || 1,
        sort: tableOptions?.sort || "daysLeft",
        order: tableOptions?.order || "desc",
        subscriptionFilter,
        limit: tableOptions?.limit || 10,
        customSort: true,
      };

      let scope;
      let masterId;
      let coachId;

      if (
        user.subscriptionType?.includes("enterprise_master") &&
        user.enterpriseAccountId
      ) {
        scope = "enterprise";
        masterId = user.enterpriseAccountId;
      } else if (
        user.subscriptionType?.includes("studio_master") &&
        user.studioAccountId
      ) {
        scope = "studio";
        masterId = user.studioAccountId;
      } else {
        scope = "coach";
        coachId = user.id;
      }

      const response = await getStudents({
        scope,
        masterId,
        coachId,
        ...commonParams,
      });

      setStudents(response.result);
      setTotalStudents(response.total);
      setIsStudentsLoading(false);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  }, [
    getStudents,
    search,
    tableOptions?.page,
    tableOptions?.sort,
    tableOptions?.order,
    tableOptions?.subscriptionFilter,
    tableOptions?.limit,
    user?.id,
    user?.subscriptionType,
    user?.enterpriseAccountId,
    user?.studioAccountId,
  ]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleAutoRenewal = async ({ subId, isEnabled, coachId }) => {
    if (!subId || !coachId) return;
    try {
      let response;
      if (isEnabled) {
        response = await enableAutoRenewal({ subId, coachId });
      } else {
        response = await disableAutoRenewal({ subId, coachId });
      }
      await fetchCoachCreditOverview({ coachId: user.id });

      setStudents((prev) => {
        return prev.map((el) => {
          if (el?.subscription?.id === subId) {
            return {
              ...el,
              subscription: { ...el.subscription, autoRenewal: isEnabled },
            };
          } else {
            return el;
          }
        });
      });
      return response;
    } catch (error) {
      console.error("Error in switching auto renewal:", error);
    }
  };

  const handleUpgradeStudent = async ({
    studentId,
    coachId,
    coachName,
    subPlan,
    subPeriod,
    autoRenewal = false,
  }) => {
    if (!studentId || !coachId || !subPlan || !subPeriod) return;

    try {
      await upgradeStudent({
        studentId,
        coachId,
        coachName,
        subPlan,
        subPeriod,
        autoRenewal,
      });
      const sub = await userService.verifySub(studentId);
      const userData = await userService.getUserData(studentId);
      await fetchUser({ coachId });
      setStudents((prev) => {
        return prev.map((el) => {
          if (el?.id === studentId) {
            return {
              ...userData,
              daysLeft: sub?.data
                ? (sub?.data?.endDate - Date.now() > 0
                    ? sub?.data?.endDate - Date.now()
                    : 0) / 86400000
                : 0,
              subscription: sub?.data,
              uid: studentId,
            };
          } else {
            return el;
          }
        });
      });
    } catch (error) {
      console.error("Error handleUpgradeStudent:", error);
    }
  };

  return {
    students,
    isStudentsLoading,
    totalStudents,
    handleAutoRenewal,
    handleUpgradeStudent,
    fetchStudents,
  };
};

export default useStudents;
